/**
 * VerdictBadge — Closed-loop workout verdict indicator.
 * Shows pass/fail/partial/substituted/DNF status after workout analysis.
 */

import { cn } from '@/lib/utils';

type VerdictType = 'pass' | 'fail' | 'partial' | 'substituted' | 'DNF';

interface VerdictBadgeProps {
  verdict: string;
  confidence?: number | null;
  size?: 'sm' | 'md';
  className?: string;
}

const verdictConfig: Record<VerdictType, {
  label: string;
  dotClass: string;
  textClass: string;
  bgClass: string;
}> = {
  pass: {
    label: 'ON TARGET',
    dotClass: 'bg-green-400',
    textClass: 'text-green-400',
    bgClass: 'bg-green-400/10 border-green-400/20',
  },
  fail: {
    label: 'TOO HARD',
    dotClass: 'bg-red-400',
    textClass: 'text-red-400',
    bgClass: 'bg-red-400/10 border-red-400/20',
  },
  partial: {
    label: 'PARTIAL',
    dotClass: 'bg-amber-400',
    textClass: 'text-amber-400',
    bgClass: 'bg-amber-400/10 border-amber-400/20',
  },
  substituted: {
    label: 'SWAPPED',
    dotClass: 'bg-blue-400',
    textClass: 'text-blue-400',
    bgClass: 'bg-blue-400/10 border-blue-400/20',
  },
  DNF: {
    label: 'INCOMPLETE',
    dotClass: 'bg-red-400',
    textClass: 'text-red-400',
    bgClass: 'bg-red-400/10 border-red-400/20',
  },
};

function normalizeVerdict(raw: string): VerdictType {
  const lower = raw.toLowerCase();
  if (lower === 'pass' || lower === 'on_target') return 'pass';
  if (lower === 'fail' || lower === 'too_hard') return 'fail';
  if (lower === 'partial') return 'partial';
  if (lower === 'substituted' || lower === 'swapped') return 'substituted';
  if (lower === 'dnf' || lower === 'incomplete') return 'DNF';
  return 'partial';
}

export function VerdictBadge({ verdict, confidence, size = 'sm', className }: VerdictBadgeProps) {
  const type = normalizeVerdict(verdict);
  const config = verdictConfig[type];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border font-mono uppercase tracking-wider',
        config.bgClass,
        config.textClass,
        size === 'sm' ? 'text-[8px] px-1.5 py-0 h-4' : 'text-[10px] px-2 py-0.5 h-5',
        className,
      )}
      title={confidence != null ? `Confidence: ${Math.round(confidence * 100)}%` : undefined}
    >
      <span className={cn('rounded-full shrink-0', config.dotClass, size === 'sm' ? 'w-1 h-1' : 'w-1.5 h-1.5')} />
      {config.label}
    </span>
  );
}
